import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n";
import { useAuth, DEMO_ACCOUNTS } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import { Lu } from "@/components/shared";
import { BrandWordmark, ThemeToggle } from "@/components/Brand";
import { AssistantChat } from "@/components/AssistantChat";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", icon: "layout-dashboard", key: "dashboard", roles: ["owner", "manager", "super_admin"] },
  { to: "/rooms", icon: "grid-3x3", key: "rooms", roles: ["owner", "manager", "super_admin"] },
  { to: "/energy", icon: "zap", key: "energy", roles: ["owner", "manager", "super_admin"] },
  { to: "/occupancy", icon: "users-round", key: "occupancy", roles: ["owner", "manager", "super_admin"] },
  { to: "/alerts", icon: "bell-ring", key: "alerts", roles: ["owner", "manager", "super_admin", "housekeeping"] },
  { to: "/scenes", icon: "sparkles", key: "scenes", roles: ["owner", "manager", "super_admin"] },
  { to: "/tickets", icon: "ticket", key: "tickets", roles: ["owner", "manager", "super_admin", "housekeeping"] },
  { to: "/properties", icon: "building-2", key: "properties", roles: ["owner", "super_admin"] },
  { to: "/leaderboard", icon: "trophy", key: "leaderboard", roles: ["owner", "super_admin", "manager"] },
  { to: "/users", icon: "user-cog", key: "users", roles: ["owner", "manager", "super_admin"] },
  { to: "/reports", icon: "file-text", key: "reports", roles: ["owner", "manager", "super_admin"] },
  { to: "/staff-app", icon: "smartphone", key: "staffApp", roles: ["owner", "manager", "super_admin", "housekeeping"] },
  { to: "/guest-app", icon: "bed-single", key: "guestApp", roles: ["owner", "manager", "super_admin"] },
  { to: "/super-admin", icon: "shield-check", key: "superAdmin", roles: ["super_admin", "owner"] },
  { to: "/investor", icon: "trending-up", key: "investor", roles: ["owner", "super_admin", "manager"] },
  { to: "/digest", icon: "mail", key: "digest", roles: ["owner", "super_admin", "manager"] },
];

export default function Layout({ children }) {
  const { t, lang, toggle } = useLang();
  const { user, logout } = useAuth();
  const { properties, selected, setSelectedId } = useProperty();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const items = NAV.filter((n) => n.roles.includes(user.role));

  const Sidebar = (
    <aside className="w-64 shrink-0 h-screen bg-card border-border ltr:border-r rtl:border-l flex flex-col fixed top-0 rtl:right-0 ltr:left-0 z-40">
      <div className="h-16 flex items-center px-5 border-b border-border">
        <BrandWordmark />
      </div>
      <nav className="flex-1 overflow-y-auto md-scrollbar-hidden py-4 px-3 space-y-1">
        {items.map((n) => (
          <NavLink
            key={n.to} to={n.to} onClick={() => setOpen(false)}
            data-testid={`nav-${n.key}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "md-gold-gradient text-primary-foreground font-semibold shadow-[0_6px_18px_rgba(0,71,255,0.28)]" : "text-muted-foreground hover:text-foreground hover:bg-foreground/5"
              }`
            }
          >
            <Lu name={n.icon} className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
            {t(n.key)}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-2 px-2 py-2 rounded-lg bg-foreground/5">
          <span className="relative flex w-2 h-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          <p className="text-[11px] text-muted-foreground leading-tight">{t("tuyaConnected")}</p>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <div className="hidden lg:block">{Sidebar}</div>
      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setOpen(false)} />
            <motion.div initial={{ x: lang === "ar" ? 260 : -260 }} animate={{ x: 0 }} exit={{ x: lang === "ar" ? 260 : -260 }}
              transition={{ type: "tween", duration: 0.25 }} className="lg:hidden">
              {Sidebar}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div className="lg:rtl:mr-64 lg:ltr:ml-64">
        {/* Header */}
        <header className="h-16 sticky top-0 z-30 md-glass flex items-center justify-between gap-3 px-4 md:px-6 border-b border-border">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-foreground/80" onClick={() => setOpen(true)} data-testid="menu-toggle">
              <Lu name="menu" className="w-6 h-6" />
            </button>
            {properties.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="property-switcher"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground/5 hover:bg-foreground/10 border border-border text-sm transition-colors">
                    <Lu name="building-2" className="w-4 h-4 text-primary" />
                    <span className="text-foreground font-medium max-w-[140px] truncate">
                      {selected ? selected[lang === "ar" ? "name_ar" : "name_en"] : "..."}
                    </span>
                    <Lu name="chevrons-up-down" className="w-3.5 h-3.5 text-muted-foreground/70" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-popover border-border text-foreground">
                  <DropdownMenuLabel>{t("properties")}</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-foreground/10" />
                  {properties.map((p) => (
                    <DropdownMenuItem key={p.id} data-testid={`prop-opt-${p.id}`}
                      onClick={() => setSelectedId(p.id)} className="cursor-pointer focus:bg-foreground/10">
                      <div className="flex flex-col">
                        <span className="font-medium">{p[lang === "ar" ? "name_ar" : "name_en"]}</span>
                        <span className="text-xs text-muted-foreground/70">{p[lang === "ar" ? "type_ar" : "type_en"]} · {p.plan}</span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button data-testid="language-toggle-btn" onClick={toggle}
              className="px-3 py-2 rounded-lg bg-foreground/5 hover:bg-foreground/10 border border-border text-sm font-semibold text-foreground/80 transition-colors">
              {lang === "ar" ? "EN" : "ع"}
            </button>

            {/* Role quick switch */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="role-switcher"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground/5 hover:bg-foreground/10 border border-border text-sm transition-colors">
                  <Lu name="repeat" className="w-4 h-4 text-primary" />
                  <span className="hidden sm:inline text-foreground/80 text-xs">{lang === "ar" ? "تبديل الدور" : "Switch role"}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-popover border-border text-foreground">
                <DropdownMenuLabel>{t("demoAccounts")}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-foreground/10" />
                <RoleSwitchItems />
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 pl-2">
                  <div className="w-9 h-9 rounded-full md-gold-gradient flex items-center justify-center text-primary-foreground font-bold text-sm">
                    {(user.name || "U")[0]}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-popover border-border text-foreground">
                <DropdownMenuLabel>
                  <p className="font-semibold">{user.name}</p>
                  <p className="text-xs text-muted-foreground/70 font-normal">{user.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-foreground/10" />
                <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="cursor-pointer text-rose-400 focus:bg-foreground/10 focus:text-rose-400">
                  <Lu name="log-out" className="w-4 h-4 ltr:mr-2 rtl:ml-2" /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main key={loc.pathname} className="p-4 md:p-8 md-fade-up max-w-[1600px] mx-auto">{children}</main>
      </div>
      {user.role !== "housekeeping" && <AssistantChat />}
    </div>
  );
}

function RoleSwitchItems() {
  const { lang } = useLang();
  const { loginAs } = useAuth();
  return DEMO_ACCOUNTS.map((a) => (
    <DropdownMenuItem key={a.role} data-testid={`switch-${a.role}`}
      onClick={() => loginAs(a.email)} className="cursor-pointer focus:bg-foreground/10">
      <Lu name={a.icon} className="w-4 h-4 ltr:mr-2 rtl:ml-2 text-primary" />
      {a[lang === "ar" ? "label_ar" : "label_en"]}
    </DropdownMenuItem>
  ));
}
